import { lazy, Suspense, useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom'
import {
  ROLES_2FA_OBLIGATOIRE,
  ROLES_MEDICAUX,
  estLeSuperAdmin,
  ROLES_TABLEAU_DE_BORD,
  type Fonctions,
  type Permission,
  type Role,
  type UtilisateurCourant,
} from '@afs/shared'
import { FrontiereErreur } from '../shared/ui/FrontiereErreur.js'
import { useFermerSession, useSession } from '../modules/socle-auth/session.js'
import { clesAuth } from '../modules/socle-auth/api.js'
import { surSessionFermee } from '../shared/api/client.js'
import { useFonctions } from '../shared/fonctions.js'
import { useTempsReel } from '../shared/notifications/tempsReel.js'
import { PAGE_COMPTES_DU_CENTRE, PAGE_MON_COMPTE, pageDAccueil } from './pageDAccueil.js'
import { CHEMIN_MES_SIGNALEMENTS } from '../shared/signalement/mesSignalements.js'
import { Connexion } from '../modules/socle-auth/pages/Connexion.js'
import { VerificationDeuxFacteurs } from '../modules/socle-auth/pages/VerificationDeuxFacteurs.js'
import { PremiereConnexion } from '../modules/socle-auth/pages/PremiereConnexion.js'
import { MotDePasseOublie } from '../modules/socle-auth/pages/MotDePasseOublie.js'
import { ReinitialisationMotDePasse } from '../modules/socle-auth/pages/ReinitialisationMotDePasse.js'
import { ChoixPoste } from '../modules/socle-auth/pages/ChoixPoste.js'

/**
 * Le socle d'authentification n'est pas chargé paresseusement : c'est le
 * chemin critique, tout le monde y passe. Les modules métier, eux, le seront
 * — un agent d'accueil n'a pas à télécharger le module Recouvrement
 * (skill `frontend-react-afs` § Structure).
 */
const MonCompte = lazy(() =>
  import('../modules/socle-auth/pages/MonCompte.js').then((m) => ({ default: m.MonCompte })),
)
// Chargé paresseusement comme le reste : la cloche suffit au quotidien,
// cet écran ne s'ouvre que pour remonter le temps.
const MesNotifications = lazy(() =>
  import('../modules/socle-notifications/pages/MesNotifications.js').then((m) => ({
    default: m.MesNotifications,
  })),
)
const MesSignalements = lazy(() =>
  import('../modules/socle-auth/pages/MesSignalements.js').then((m) => ({
    default: m.MesSignalements,
  })),
)
const EnrolementDeuxFacteurs = lazy(() =>
  import('../modules/socle-auth/pages/EnrolementDeuxFacteurs.js').then((m) => ({
    default: m.EnrolementDeuxFacteurs,
  })),
)

// L'écran d'accueil est le poste de travail de l'agent : chargé paresseusement
// comme les autres modules métier, mais c'est celui qui arrivera le plus souvent
// en premier une fois le parcours patient complet.
const Accueil = lazy(() =>
  import('../modules/parcours-patient/pages/Accueil.js').then((m) => ({ default: m.Accueil })),
)
const FichePatient = lazy(() =>
  import('../modules/parcours-patient/pages/FichePatient.js').then((m) => ({
    default: m.FichePatient,
  })),
)
// CP-12 : l'accueil sans réseau. Hors session — il ne parle jamais au serveur.
const AccueilHorsLigne = lazy(() =>
  import('../modules/hors-ligne/pages/AccueilHorsLigne.js').then((m) => ({
    default: m.AccueilHorsLigne,
  })),
)
// La télé de salle d'attente (ADR 0012) : hors de la coque et sans session.
const EcranAppel = lazy(() =>
  import('../modules/ecran-appel/pages/EcranAppel.js').then((m) => ({
    default: m.EcranAppel,
  })),
)
const FormulairesCoupure = lazy(() =>
  import('../modules/parcours-patient/pages/FormulairesCoupure.js').then((m) => ({
    default: m.FormulairesCoupure,
  })),
)
const RessaisieCoupure = lazy(() =>
  import('../modules/coupure/pages/RessaisieCoupure.js').then((m) => ({
    default: m.RessaisieCoupure,
  })),
)
const GrilleCoupure = lazy(() =>
  import('../modules/coupure/pages/GrilleCoupure.js').then((m) => ({ default: m.GrilleCoupure })),
)
const Doublons = lazy(() =>
  import('../modules/parcours-patient/pages/Doublons.js').then((m) => ({ default: m.Doublons })),
)
const ComparaisonFiches = lazy(() =>
  import('../modules/parcours-patient/pages/ComparaisonFiches.js').then((m) => ({
    default: m.ComparaisonFiches,
  })),
)
const Caisse = lazy(() =>
  import('../modules/caisse/pages/Caisse.js').then((m) => ({ default: m.Caisse })),
)
const FileAConfirmer = lazy(() =>
  import('../modules/parcours-patient/pages/FileAConfirmer.js').then((m) => ({
    default: m.FileAConfirmer,
  })),
)
const AttenteChargee = lazy(() =>
  import('../modules/parcours-patient/pages/AttenteChargee.js').then((m) => ({
    default: m.AttenteChargee,
  })),
)
const EcartsOffre = lazy(() =>
  import('../modules/parcours-patient/pages/EcartsOffre.js').then((m) => ({
    default: m.EcartsOffre,
  })),
)
const NouveauPatient = lazy(() =>
  import('../modules/parcours-patient/pages/NouveauPatient.js').then((m) => ({
    default: m.NouveauPatient,
  })),
)

// L'import DNS n'est ouvert qu'à la chargée de clientèle.
const ImportsDns = lazy(() =>
  import('../modules/import-dns/pages/ImportsDns.js').then((m) => ({ default: m.ImportsDns })),
)
const RapportDns = lazy(() =>
  import('../modules/import-dns/pages/RapportDns.js').then((m) => ({ default: m.RapportDns })),
)
const SaisieDns = lazy(() =>
  import('../modules/import-dns/pages/SaisieDns.js').then((m) => ({ default: m.SaisieDns })),
)
const FeuillesDns = lazy(() =>
  import('../modules/import-dns/pages/FeuillesDns.js').then((m) => ({ default: m.FeuillesDns })),
)
const MappingDns = lazy(() =>
  import('../modules/import-dns/pages/MappingDns.js').then((m) => ({ default: m.MappingDns })),
)

// Le référentiel des entreprises : la chargée de clientèle, la comptable
// et la Direction — jamais un poste d'accueil.
const Entreprises = lazy(() =>
  import('../modules/entreprises/pages/Entreprises.js').then((m) => ({ default: m.Entreprises })),
)
const NouvelleEntreprise = lazy(() =>
  import('../modules/entreprises/pages/NouvelleEntreprise.js').then((m) => ({
    default: m.NouvelleEntreprise,
  })),
)
const FicheEntreprise = lazy(() =>
  import('../modules/entreprises/pages/FicheEntreprise.js').then((m) => ({
    default: m.FicheEntreprise,
  })),
)
const Offres = lazy(() =>
  import('../modules/entreprises/pages/Offres.js').then((m) => ({ default: m.Offres })),
)
// Le registre du jour est du parcours patient ; les affluences, du pilotage.
// Décision 130 : la masse salariale des adhérents, `salaire:read` seulement.
const DnsSalaires = lazy(() =>
  import('../modules/dns-salaires/pages/DnsSalaires.js').then((m) => ({ default: m.DnsSalaires })),
)
const RegistreDuJour = lazy(() =>
  import('../modules/parcours-patient/pages/RegistreDuJour.js').then((m) => ({
    default: m.RegistreDuJour,
  })),
)
const FileAttente = lazy(() =>
  import('../modules/parcours-patient/pages/FileAttente.js').then((m) => ({
    default: m.FileAttente,
  })),
)
const MaFile = lazy(() =>
  import('../modules/parcours-patient/pages/MaFile.js').then((m) => ({ default: m.MaFile })),
)
const CaisseDirection = lazy(() =>
  import('../modules/caisse/pages/CaisseDirection.js').then((m) => ({
    default: m.CaisseDirection,
  })),
)
const Affluences = lazy(() =>
  import('../modules/pilotage/pages/Affluences.js').then((m) => ({ default: m.Affluences })),
)
const TableauDeBord = lazy(() =>
  import('../modules/pilotage/pages/TableauDeBord.js').then((m) => ({
    default: m.TableauDeBord,
  })),
)
const Journee = lazy(() =>
  import('../modules/pilotage/pages/Journee.js').then((m) => ({ default: m.Journee })),
)
const Tendance = lazy(() =>
  import('../modules/pilotage/pages/Tendance.js').then((m) => ({ default: m.Tendance })),
)
const StatistiquesConfirmation = lazy(() =>
  import('../modules/pilotage/pages/StatistiquesConfirmation.js').then((m) => ({
    default: m.StatistiquesConfirmation,
  })),
)
const SuiviDns = lazy(() =>
  import('../modules/pilotage/pages/SuiviDns.js').then((m) => ({ default: m.SuiviDns })),
)
const ActiviteMedecins = lazy(() =>
  import('../modules/pilotage/pages/ActiviteMedecins.js').then((m) => ({
    default: m.ActiviteMedecins,
  })),
)

const ComptesDuCentre = lazy(() =>
  import('../modules/centre/pages/ComptesDuCentre.js').then((m) => ({
    default: m.ComptesDuCentre,
  })),
)

// L'administration ne concerne qu'une poignée de comptes : elle ne doit pas
// peser sur le chargement d'un poste d'accueil.
const Utilisateurs = lazy(() =>
  import('../modules/administration/pages/Utilisateurs.js').then((m) => ({
    default: m.Utilisateurs,
  })),
)
const RolesAcces = lazy(() =>
  import('../modules/administration/pages/RolesAcces.js').then((m) => ({ default: m.RolesAcces })),
)
const Sites = lazy(() =>
  import('../modules/administration/pages/Sites.js').then((m) => ({ default: m.Sites })),
)
const JournalConnexions = lazy(() =>
  import('../modules/administration/pages/JournalConnexions.js').then((m) => ({
    default: m.JournalConnexions,
  })),
)
const Sauvegardes = lazy(() =>
  import('../modules/administration/pages/Sauvegardes.js').then((m) => ({
    default: m.Sauvegardes,
  })),
)
const Signalements = lazy(() =>
  import('../modules/administration/pages/Signalements.js').then((m) => ({
    default: m.Signalements,
  })),
)
const Notifications = lazy(() =>
  import('../modules/administration/pages/Notifications.js').then((m) => ({
    default: m.Notifications,
  })),
)
const Configuration = lazy(() =>
  import('../modules/administration/pages/Configuration.js').then((m) => ({
    default: m.Configuration,
  })),
)

/**
 * Garde de route.
 *
 * ⚠️ Elle **masque**, elle ne protège pas. Chaque route de l'API revérifie
 * l'authentification et le périmètre côté serveur, toujours
 * (skill `securite-donnees-sante`). Contourner cette garde dans le navigateur
 * ne donne accès à aucune donnée.
 */
/** Exportée pour ses tests : la garde des routes. */
export function Protege({
  children,
  exigeSecondFacteur = true,
  permission,
  fonction,
  roles,
  superAdminNatif = false,
}: {
  children: React.JSX.Element
  /**
   * `false` pour l'écran d'enrôlement lui-même : il réclame une session, mais
   * pas un second facteur déjà actif — sinon la redirection boucle.
   */
  exigeSecondFacteur?: boolean
  /**
   * Droit exigé pour atteindre l'écran. Comme le reste de cette garde, il
   * MASQUE : l'API revérifie et répondra 403 de toute façon.
   */
  permission?: Permission
  /**
   * Une fonction hors v1 (décision du 13/10) : éteinte, l'écran n'existe
   * pas — même sort qu'une adresse inconnue, renvoyée à l'accueil.
   */
  fonction?: keyof Fonctions
  /** Réservé à ces rôles de base, en plus de la permission (tableau de bord : Direction seule). */
  roles?: readonly Role[]
  /** Ouvert aussi à LE Super administrateur natif, jamais à un dérivé de sa base (432, 433). */
  superAdminNatif?: boolean
}): React.JSX.Element {
  const { utilisateur, chargement } = useSession()
  const { fonctions, chargement: fonctionsEnChargement } = useFonctions()

  // Ni « connecté » ni « déconnecté » tant que la restauration de session
  // n'a pas répondu : rediriger ici renverrait tout le monde vers la
  // connexion à chaque rechargement de page.
  // `superAdminNatif` lit aussi un interrupteur (décision 436) : sans attendre
  // les fonctions, un rechargement de Ma file renvoyait le Super administrateur
  // à « / » (revue S1053, AC1).
  if (chargement || ((fonction !== undefined || superAdminNatif) && fonctionsEnChargement)) {
    return <EcranAttente />
  }
  if (!utilisateur) return <Navigate to="/connexion" replace />

  // Le CCT § 5.1 impose le second facteur à ces rôles. Tant qu'il n'est pas
  // activé, il n'y a qu'un seul écran accessible : celui qui l'active.
  //
  // Filet de sécurité uniquement : depuis le correctif serveur, la connexion
  // n'ouvre AUCUNE session à un compte dans ce cas — il n'y a donc normalement
  // personne à rediriger ici. La garde reste pour le compte dont le rôle est
  // élevé pendant une session déjà ouverte.
  const doitEnroler = ROLES_2FA_OBLIGATOIRE.includes(utilisateur.role) && !utilisateur.totpActive
  if (exigeSecondFacteur && doitEnroler) {
    return <Navigate to="/second-facteur/activation" replace />
  }

  // Renvoi vers l'accueil plutôt qu'un écran « accès refusé » : l'utilisateur
  // n'a pas cherché à forcer une porte, il a suivi un lien ou un signet.
  // … mais en le disant : un lien partagé qui retombait en silence sur une
  // autre page laissait croire à une panne.
  if (
    (permission !== undefined && !utilisateur.permissions.includes(permission)) ||
    (roles !== undefined &&
      !roles.includes(utilisateur.role) &&
      !(superAdminNatif && fonctions.superAdminPartout && estLeSuperAdmin(utilisateur)))
  ) {
    return <Navigate to="/" replace state={{ accesRefuse: true }} />
  }

  if (fonction !== undefined && !fonctions[fonction]) return <Navigate to="/" replace />

  return children
}

/**
 * La connexion poussée, une seule pour toute la session. Elle vivait dans la
 * cloche, que chaque écran remonte : une navigation fermait et rouvrait la
 * socket, et ce qui arrivait pendant la reconnexion se perdait. Ici, elle ne
 * se remonte pas d'un écran à l'autre. Hors des pages publiques seulement :
 * sur l'écran de connexion, lire la session tenterait un rafraîchissement.
 */
function TempsReelDeSession(): React.JSX.Element | null {
  const { pathname } = useLocation()
  return /^\/(connexion|mot-de-passe-oublie|hors-ligne|ecran-appel)/.test(pathname) ? null : (
    <SocketDeSession />
  )
}

function SocketDeSession(): null {
  const { utilisateur } = useSession()
  useTempsReel(utilisateur !== undefined)
  return null
}

/**
 * Décision 417 — la session a été close côté serveur (inactivité, autre poste,
 * révocation) : une seule fois, on vide tout ce que l'onglet en garde et on
 * renvoie à la connexion avec le motif. Sans ça, l'agent restait dans
 * l'application, chaque écran affichant sa propre erreur, jusqu'à recharger.
 */
export function RenvoiSessionFermee(): null {
  const naviguer = useNavigate()
  const client = useQueryClient()
  const fermerSession = useFermerSession()

  useEffect(
    () =>
      surSessionFermee((motif) => {
        const email = client.getQueryData<UtilisateurCourant | null>(clesAuth.session())?.email
        fermerSession()
        void naviguer('/connexion', { replace: true, state: { sessionFermee: motif, email } })
      }),
    [client, fermerSession, naviguer],
  )
  return null
}

/**
 * `/connexion` n'a de sens que déconnecté : une session déjà ouverte mène à
 * l'écran de travail du rôle, comme après s'être connecté.
 */
function SeulementDeconnecte({ children }: { children: React.JSX.Element }): React.JSX.Element {
  const { utilisateur, chargement } = useSession()
  // Tant que la restauration n'a pas répondu, on ne sait pas encore.
  if (chargement) return <EcranAttente />
  if (utilisateur) return <Navigate to="/" replace />
  return children
}

/** `/` : l'écran de travail du rôle, en gardant l'éventuel « accès refusé ». */
export function AccueilDuRole(): React.JSX.Element {
  const { utilisateur } = useSession()
  const { fonctions, chargement } = useFonctions()
  const location = useLocation()
  const state = location.state as unknown
  // La page d'arrivée de la Direction dépend d'un interrupteur : on l'attend.
  if (chargement) return <EcranAttente />
  const page = pageDAccueil(utilisateur?.permissions ?? [], { role: utilisateur?.role, fonctions })
  return <Navigate to={page} replace state={state} />
}

/** Écran d'attente neutre — ni contenu, ni redirection prématurée. */
function EcranAttente(): React.JSX.Element {
  return <div aria-busy="true" aria-live="polite" />
}

export function Application(): React.JSX.Element {
  return (
    <BrowserRouter>
      {/*
        La frontière est DANS le routeur, pas autour : c'est ce qui lui
        permet de ramener à l'accueil plutôt que de laisser l'utilisateur
        sur un écran mort dont il ne peut plus sortir.

        Elle enveloppe aussi `Suspense` : un module paresseux qui ne se
        charge pas — déploiement pendant que l'onglet était ouvert — lève au
        même endroit qu'une exception de rendu.
      */}
      <FrontiereRoutee>
        <TempsReelDeSession />
        <RenvoiSessionFermee />
        <Suspense fallback={<EcranAttente />}>
          <Routes>
            <Route
              path="/connexion"
              element={
                <SeulementDeconnecte>
                  <Connexion />
                </SeulementDeconnecte>
              }
            />
            <Route path="/hors-ligne" element={<AccueilHorsLigne />} />
            <Route path="/ecran-appel" element={<EcranAppel />} />
            <Route path="/connexion/second-facteur" element={<VerificationDeuxFacteurs />} />
            <Route path="/connexion/premiere-connexion" element={<PremiereConnexion />} />
            {/*
              Le poste du jour se déclare APRÈS le mot de passe et le second
              facteur : l'écran n'est atteignable qu'avec le jeton d'étape,
              transmis par la navigation (ADR 0002).
            */}
            <Route path="/connexion/poste" element={<ChoixPoste />} />

            {/*
              Publiques, et elles doivent l'être : elles s'adressent à
              quelqu'un qui ne peut pas se connecter. Le jeton du second
              écran arrive par l'URL, seul canal qu'un lien de courriel
              offre — l'API le limite à un usage et à trente minutes.
            */}
            <Route path="/mot-de-passe-oublie" element={<MotDePasseOublie />} />
            <Route path="/mot-de-passe-oublie/:jeton" element={<ReinitialisationMotDePasse />} />

            <Route
              path="/second-facteur/activation"
              element={
                <Protege exigeSecondFacteur={false}>
                  <EnrolementDeuxFacteurs />
                </Protege>
              }
            />

            {/* Chacun arrive là où il travaille ; « Mon compte » a sa propre adresse. */}
            <Route
              path="/"
              element={
                <Protege>
                  <AccueilDuRole />
                </Protege>
              }
            />
            <Route
              path={PAGE_MON_COMPTE}
              element={
                <Protege>
                  <MonCompte />
                </Protege>
              }
            />
            {/* Décision 449 : le chef de centre gère les comptes de son site. */}
            <Route
              path={PAGE_COMPTES_DU_CENTRE}
              element={
                <Protege permission="utilisateur:write_centre">
                  <ComptesDuCentre />
                </Protege>
              }
            />

            {/*
              Aucune permission : l'écran ne montre que ce qui est adressé à
              l'appelant, comme « mon compte ». Le serveur tire le
              destinataire du jeton, jamais de la requête.
            */}
            <Route
              path="/notifications"
              element={
                <Protege>
                  <MesNotifications />
                </Protege>
              }
            />
            {/* Décision 177 : tout compte relit ses signalements ; le serveur tire l'auteur du jeton. */}
            <Route
              path={CHEMIN_MES_SIGNALEMENTS}
              element={
                <Protege>
                  <MesSignalements />
                </Protege>
              }
            />

            <Route
              path="/accueil"
              element={
                <Protege permission="patient:read">
                  <Accueil />
                </Protege>
              }
            />
            {/* AJ-03 : l'accueil voit qui attend la chargée à son comptoir, et depuis quand. */}
            <Route
              path="/accueil/attente-chargee"
              element={
                <Protege permission="eligibilite:check" roles={['AGENT_ACCUEIL']} superAdminNatif>
                  <AttenteChargee />
                </Protege>
              }
            />
            <Route
              path="/parcours/a-confirmer"
              element={
                <Protege permission="patient:confirm">
                  <FileAConfirmer />
                </Protege>
              }
            />
            <Route
              path="/parcours/ecarts-offre"
              element={
                <Protege permission="patient:confirm">
                  <EcartsOffre />
                </Protege>
              }
            />
            {/* CP-12 : les formulaires papier d'une coupure — pour qui tient le registre. */}
            <Route
              path="/parcours/formulaires-coupure"
              element={
                <Protege permission="file_attente:write" fonction="formulairesCoupure">
                  <FormulairesCoupure />
                </Protege>
              }
            />
            {/* CP-12 : scanner une feuille de coupure remplie à la main, puis
                l'inscrire au registre à l'heure du papier. */}
            <Route
              path="/parcours/coupure/ressaisie"
              element={
                <Protege permission="file_attente:write" fonction="scanCoupure">
                  <RessaisieCoupure />
                </Protege>
              }
            />
            <Route
              path="/parcours/coupure/:id"
              element={
                <Protege permission="file_attente:write" fonction="scanCoupure">
                  <GrilleCoupure />
                </Protege>
              }
            />
            {/* Fusion de dossiers : une décision d'identité, pas un geste de guichet. */}
            <Route
              path="/parcours/doublons"
              element={
                <Protege permission="patient:merge">
                  <Doublons />
                </Protege>
              }
            />
            <Route
              path="/parcours/doublons/comparer"
              element={
                <Protege permission="patient:merge">
                  <ComparaisonFiches />
                </Protege>
              }
            />
            <Route
              path="/accueil/patients/:id"
              element={
                <Protege permission="patient:read">
                  <FichePatient />
                </Protege>
              }
            />
            <Route
              path="/accueil/nouveau"
              element={
                <Protege permission="patient:write">
                  <NouveauPatient />
                </Protege>
              }
            />

            {/*
              `dns:import` sur les deux routes : c'est l'entrée du menu. Le
              rapport se lit avec `dns:read`, mais seul qui dépose y a un
              geste à faire ; la validation, elle, exige `dns:validate`.
            */}
            <Route
              path="/dns"
              element={
                <Protege permission="dns:import">
                  <ImportsDns />
                </Protege>
              }
            />
            <Route
              path="/dns/:id"
              element={
                <Protege permission="dns:import">
                  <RapportDns />
                </Protege>
              }
            />
            <Route
              path="/dns/:id/saisie"
              element={
                <Protege permission="dns:import">
                  <SaisieDns />
                </Protege>
              }
            />
            <Route
              path="/dns/:id/feuilles"
              element={
                <Protege permission="dns:import">
                  <FeuillesDns />
                </Protege>
              }
            />
            <Route
              path="/dns/:id/mapping"
              element={
                <Protege permission="dns:import">
                  <MappingDns />
                </Protege>
              }
            />

            {/*
              `/entreprises/nouvelle` avant `/entreprises/:id` : un chemin
              fixe ne doit pas être pris pour un identifiant.
            */}
            <Route
              path="/entreprises"
              element={
                <Protege permission="adherent:read">
                  <Entreprises />
                </Protege>
              }
            />
            <Route
              path="/entreprises/nouvelle"
              element={
                <Protege permission="adherent:write">
                  <NouvelleEntreprise />
                </Protege>
              }
            />
            <Route
              path="/entreprises/:id"
              element={
                <Protege permission="adherent:read">
                  <FicheEntreprise />
                </Protege>
              }
            />
            <Route
              path="/offres"
              element={
                <Protege permission="adherent:read">
                  <Offres />
                </Protege>
              }
            />
            <Route
              path="/dns-salaires"
              element={
                <Protege permission="salaire:read">
                  <DnsSalaires />
                </Protege>
              }
            />
            <Route
              path="/parcours/file-attente"
              element={
                <Protege permission="file_attente:read">
                  <FileAttente />
                </Protege>
              }
            />
            <Route
              path="/parcours/ma-file"
              element={
                <Protege permission="dossier_medical:write" roles={ROLES_MEDICAUX} superAdminNatif>
                  <MaFile />
                </Protege>
              }
            />
            <Route
              path="/parcours/registre"
              element={
                <Protege permission="file_attente:read">
                  <RegistreDuJour />
                </Protege>
              }
            />
            {/* R14 — la caisse des centres, en lecture, pour la Direction et le responsable de site. */}
            <Route
              path="/pilotage/caisse"
              element={
                <Protege permission="caisse:read" fonction="caisse">
                  <CaisseDirection />
                </Protege>
              }
            />
            <Route
              path="/pilotage/tableau-de-bord"
              element={
                <Protege
                  permission="dashboard:read"
                  fonction="tableauDeBord"
                  roles={ROLES_TABLEAU_DE_BORD}
                >
                  <TableauDeBord />
                </Protege>
              }
            />
            <Route
              path="/pilotage/tableau-de-bord/journee/:siteId"
              element={
                <Protege
                  permission="dashboard:read"
                  fonction="tableauDeBord"
                  roles={ROLES_TABLEAU_DE_BORD}
                >
                  <Journee />
                </Protege>
              }
            />
            <Route
              path="/pilotage/tableau-de-bord/tendance/:siteId"
              element={
                <Protege
                  permission="dashboard:read"
                  fonction="tableauDeBord"
                  roles={ROLES_TABLEAU_DE_BORD}
                >
                  <Tendance />
                </Protege>
              }
            />
            <Route
              path="/pilotage/tableau-de-bord/confirmations"
              element={
                <Protege
                  permission="dashboard:read"
                  fonction="tableauDeBord"
                  roles={ROLES_TABLEAU_DE_BORD}
                >
                  <StatistiquesConfirmation />
                </Protege>
              }
            />
            <Route
              path="/pilotage/affluences"
              element={
                <Protege permission="dashboard:read">
                  <Affluences />
                </Protege>
              }
            />
            {/* Décision 437 : Direction, chargée et Super admin — ceux qui lisent les DNS. */}
            <Route
              path="/pilotage/suivi-dns"
              element={
                <Protege permission="dns:read">
                  <SuiviDns />
                </Protege>
              }
            />
            <Route
              path="/pilotage/activite-medecins"
              element={
                <Protege permission="activite_medecin:read" fonction="activiteMedecins">
                  <ActiviteMedecins />
                </Protege>
              }
            />

            <Route
              path="/administration/utilisateurs"
              element={
                <Protege permission="utilisateur:read">
                  <Utilisateurs />
                </Protege>
              }
            />
            {/*
              `site:write` et non `site:read` : lire le référentiel sert à
              tous les écrans qui nomment un site, mais celui-ci ne fait
              qu'ouvrir, modifier et fermer. Qui ne peut rien y faire n'a
              rien à y voir.
            */}
            <Route
              path="/administration/sites"
              element={
                <Protege permission="site:write">
                  <Sites />
                </Protege>
              }
            />
            <Route
              path="/administration/roles"
              element={
                <Protege permission="utilisateur:read">
                  <RolesAcces />
                </Protege>
              }
            />
            <Route
              path="/administration/journal"
              element={
                <Protege permission="audit:read">
                  <JournalConnexions />
                </Protege>
              }
            />
            <Route
              path="/administration/notifications"
              element={
                <Protege permission="configuration:read">
                  <Notifications />
                </Protege>
              }
            />
            <Route
              path="/administration/signalements"
              element={
                <Protege permission="signalement:read">
                  <Signalements />
                </Protege>
              }
            />
            <Route
              path="/administration/sauvegardes"
              element={
                <Protege permission="sauvegarde:telecharger">
                  <Sauvegardes />
                </Protege>
              }
            />
            <Route
              path="/caisse"
              element={
                <Protege permission="caisse:encaisser" fonction="caisse">
                  <Caisse />
                </Protege>
              }
            />
            <Route
              path="/administration/configuration"
              element={
                <Protege permission="configuration:read">
                  <Configuration />
                </Protege>
              }
            />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </FrontiereRoutee>
    </BrowserRouter>
  )
}

/**
 * La frontière, câblée au routeur.
 *
 * `FrontiereErreur` est une classe — React n'expose pas les crochets
 * d'erreur autrement — et une classe ne peut pas appeler `useNavigate`. Ce
 * petit composant fonctionnel fait le pont.
 */
function FrontiereRoutee({ children }: { children: React.ReactNode }): React.JSX.Element {
  const naviguer = useNavigate()

  return (
    <FrontiereErreur
      onReinitialiser={() => {
        void naviguer('/', { replace: true })
      }}
    >
      {children}
    </FrontiereErreur>
  )
}
